import { apiToPlanForm, planFormToApi } from "./offerPlanAdapter";

import type { Media, Translation } from "../../api/OfferAPI.types";
import type { OfferPlanToApi } from "../../models/OfferPlan.types";
import type { OfferPlanType } from "../../types/offers";

/** Accepts a `GetOfferResponse`; `donationEnabled`/`visible`/`geoblock_mode` only ever come from form edits. */
export interface OfferFormSource {
  begin_date?: string | null;
  countries?: unknown[];
  custom_params?: unknown;
  description?: string;
  enabled?: boolean;
  end_date?: string | null;
  geoblock_mode?: string;
  inserted_at?: string;
  isFetched?: boolean;
  label?: string;
  media_preview_uuid?: string | null;
  medias?: Media[];
  offer_plans?: OfferPlanType[];
  rank?: number;
  storefront_project_uuid?: string;
  translations_indexes?: unknown;
  translations?: Translation[];
  temporal_restriction?: boolean;
  type?: string;
  updated_at?: string;
  uuid?: string;
  donationEnabled?: boolean;
  visible?: boolean;
}

export interface OfferFormState {
  begin_date?: string | null;
  countries?: unknown[];
  custom_params?: unknown;
  description?: string;
  enabled: boolean;
  end_date?: string | null;
  geoblock_mode?: string;
  inserted_at?: string;
  isFetched?: boolean;
  label: string;
  media_preview_uuid: string | null;
  medias: Media[];
  offer_plans: OfferPlanType[];
  rank: number;
  storefront_project_uuid?: string;
  translations_indexes?: unknown;
  translations: Translation[];
  temporal_restriction?: boolean;
  type?: string;
  updated_at?: string;
  uuid: string;
  donationEnabled?: boolean;
  visible?: boolean;
}

export interface OfferToApi {
  enabled: boolean;
  offer_plans: OfferPlanToApi[];
  label: string;
  media_preview_uuid: string | null;
  medias: Media[];
  translations: Translation[];
  translations_indexes?: unknown;
  uuid: string;
  donation_enabled?: boolean;
  rank: number;
}

export function apiToOfferForm(offer?: OfferFormSource): OfferFormState {
  return {
    begin_date: offer?.begin_date,
    countries: offer?.countries,
    custom_params: offer?.custom_params,
    description: offer?.description,
    enabled: offer?.enabled ?? false,
    end_date: offer?.end_date,
    geoblock_mode: offer?.geoblock_mode,
    inserted_at: offer?.inserted_at,
    isFetched: offer?.isFetched,
    label: offer?.label ?? "",
    media_preview_uuid: offer?.media_preview_uuid ?? null,
    medias: offer?.medias ?? [],
    offer_plans: offer?.offer_plans ?? [],
    rank: offer?.rank ?? 0,
    storefront_project_uuid: offer?.storefront_project_uuid,
    translations_indexes: offer?.translations_indexes,
    translations: offer?.translations ?? [],
    temporal_restriction: offer?.temporal_restriction,
    type: offer?.type,
    updated_at: offer?.updated_at,
    uuid: offer?.uuid ?? "",
    donationEnabled: offer?.donationEnabled,
    visible: offer?.visible,
  };
}

/** A new plan's client temp uuid is dropped so the backend assigns a real one. */
export function offerPlansToApi(offerPlans: OfferPlanType[]): OfferPlanToApi[] {
  return offerPlans.map((offerPlan) => {
    const apiPlan = planFormToApi(apiToPlanForm(offerPlan));
    return offerPlan.isNew ? { ...apiPlan, uuid: undefined } : apiPlan;
  });
}

/** An offer is only enabled once at least one of its plans is. */
export function validateEnabled(offer: {
  enabled: boolean;
  offer_plans: Pick<OfferPlanToApi, "enabled">[];
}): boolean {
  const enabledPlanList = offer.offer_plans.filter(
    (offerPlan) => offerPlan.enabled,
  );

  return enabledPlanList.length > 0 && offer.enabled;
}

export function offerFormToApi(offer: OfferFormState): OfferToApi {
  // Normalized plans, so that an edu plan counts as enabled.
  const offerPlans = offerPlansToApi(offer.offer_plans);

  return {
    enabled: validateEnabled({
      enabled: offer.enabled,
      offer_plans: offerPlans,
    }),
    offer_plans: offerPlans,
    label: offer.label,
    media_preview_uuid: offer.media_preview_uuid,
    medias: offer.medias,
    translations: offer.translations,
    uuid: offer.uuid,
    donation_enabled: offer.donationEnabled,
    rank: offer.rank,
  };
}
